/**
 * WeaveIn.ai Platform - Contact Detail Page (Full Enrollment Parity)
 * 8 tabs with proper integration to underlying platform capabilities:
 * - Activities: MemberWorkflowProgress + activity log
 * - Documents: DocumentRequirements + upload + signature
 * - Notes: Case notes with types/confidential/followup
 * - Goals: Category/status/target tracking
 * - Tracking: TaskList component (same as enrollment)
 * - Conversations: Email/SMS/Phone via staff_member_communications + templates + history
 * - Forms: Form instances linked to contact
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeft, Phone, Mail, Calendar, MapPin, Edit, Trash2, ArrowUpRight,
  User, Activity, FileText, StickyNote, Target, ListTodo, MessageSquare,
  ClipboardList, AlertTriangle, Plus, Loader2, Send, CheckCircle,
  Clock, Lock, Eye, Image as ImageIcon, Printer, Gift, ExternalLink
} from 'lucide-react';
import DonorGivingTab from '@/components/donors/DonorGivingTab';
import {
  contactsAPI, Contact, ContactActivity, ContactProgramStatus,
  ContactNote, ContactNoteCreate, ContactGoal, ContactGoalCreate,
  ContactDocument, ContactFormInstance, ContactWorkflow,
} from '@/services/api/contacts';
import ContactStatusPipeline from './ContactStatusPipeline';
import ContactActivityForm from './ContactActivityForm';
import ContactActivityTimeline from './ContactActivityTimeline';
import DuplicateMatchPanel from './DuplicateMatchPanel';
import PromoteEnrollDialog from './PromoteEnrollDialog';
import PromoteToMemberDialog from './PromoteToMemberDialog';

// Reuse enrollment-level components
import TaskList from '@/components/tasks/TaskList';
import { MemberWorkflowProgress } from '@/components/workflows/MemberWorkflowProgress';
import { DocumentRequirements } from '@/components/documents/DocumentRequirements';
import { DocumentUploadDialog } from '@/components/documents/DocumentUploadDialog';
import { MemberPhoneInterface } from '@/components/communications/MemberPhoneInterface';
import { workflowsAPI } from '@/services/api/workflows';
import AuditTrailPanel from '@/components/common/AuditTrailPanel';
import { HardCopyTrackingList } from '@/components/hard-copy-tracking';

import { config as productConfig } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  VariablePicker,
  TemplatePreviewButton,
  VariableInputFields,
  UnfilledMergeFields,
  insertToken,
  unfilledFailures,
  useSendInputs,
  type UnfilledSendFailure,
} from '@/components/messaging/templateVariables';
import { formPageUrl } from '@/components/forms/FormPage';
interface ContactDetailPageProps {
  contactId: number;
  onBack?: () => void;
  onEdit?: (contactId: number) => void;
  onArchived?: () => void;
  /** Fired after a contact is promoted to a member (enroll). The promoted
   *  record keeps the same id but is no longer a contact, so the parent
   *  should navigate to the member profile rather than re-fetch a contact. */
  onEnrolled?: (memberId: number) => void;
}

type TabKey = 'overview' | 'activities' | 'documents' | 'notes' | 'goals' | 'tracking' | 'hard-copy' | 'conversations' | 'forms' | 'history' | 'giving';

const getApiBaseUrl = () => (productConfig.weaveinApi.baseUrl ?? '').trim().replace(/\/$/, '');

export default function ContactDetailPage({ contactId, onBack, onEdit, onArchived, onEnrolled }: ContactDetailPageProps) {
  const { t } = useAppTranslation();
  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [error, setError] = useState<string | null>(null);
  const [promoteProgram, setPromoteProgram] = useState<ContactProgramStatus | null>(null);
  const [showPromoteToMember, setShowPromoteToMember] = useState(false);

  // Tab data
  const [activities, setActivities] = useState<ContactActivity[]>([]);
  const [notes, setNotes] = useState<ContactNote[]>([]);
  const [goals, setGoals] = useState<ContactGoal[]>([]);
  const [documents, setDocuments] = useState<ContactDocument[]>([]);
  const [forms, setForms] = useState<ContactFormInstance[]>([]);
  const [workflows, setWorkflows] = useState<ContactWorkflow[]>([]);
  const [availableTemplates, setAvailableTemplates] = useState<Array<{ id: number; name: string }>>([]);
  const [availablePrograms, setAvailablePrograms] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(null);
  const [assigningProgram, setAssigningProgram] = useState(false);

  // Conversations state
  const [selectedCommMethod, setSelectedCommMethod] = useState<'email' | 'sms' | 'phone'>('email');
  const [showMessageComposer, setShowMessageComposer] = useState(false);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  // Merge fields are filled server-side from the org's template-variable registry.
  const messageBodyRef = useRef<HTMLTextAreaElement | null>(null);
  const sendInputs = useSendInputs({ subject: selectedCommMethod === 'email' ? messageSubject : null, body: messageBody });
  const [blockedSends, setBlockedSends] = useState<UnfilledSendFailure[]>([]);
  const [sentMessages, setSentMessages] = useState<any[]>([]);
  const [sentLoading, setSentLoading] = useState(false);

  // Form visibility
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [showDocUpload, setShowDocUpload] = useState(false);

  // Form state
  const [noteForm, setNoteForm] = useState<ContactNoteCreate>({ note_text: '', note_type: 'general' });
  const [goalForm, setGoalForm] = useState<ContactGoalCreate>({ goal_text: '' });

  const loadContact = useCallback(async () => {
    setLoading(true);
    try { setContact(await contactsAPI.getContact(contactId)); }
    catch (err: any) { setError(err.response?.data?.detail || err.message); }
    finally { setLoading(false); }
  }, [contactId]);

  useEffect(() => { loadContact(); }, [loadContact]);

  // Load available programs for assign-to-program
  useEffect(() => {
    workflowsAPI.listWorkflowTemplates({ is_active: true }).catch(() => null); // warm up
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');
    const base = getApiBaseUrl();
    fetch(`${base}/api/programs`, { headers: { Authorization: `Bearer ${token}`, 'X-CBO-ID': cboId || '' } })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.programs || [];
        setAvailablePrograms(list.map((p: any) => ({ id: p.id, name: p.name })));
      })
      .catch(() => {});
  }, []);

  // Load tab data on switch
  useEffect(() => {
    const loaders: Record<string, () => Promise<void>> = {
      activities: async () => {
        const programId = contact?.program_statuses?.[0]?.program_id;
        const [actRes, wfRes] = await Promise.all([
          contactsAPI.listContactActivities(contactId),
          contactsAPI.listWorkflows(contactId),
        ]);
        setActivities(actRes.items);
        setWorkflows(wfRes);
        // Fetch available templates for "Start Workflow" buttons
        // Include templates for the contact's program AND templates with no program restriction
        try {
          const tmplRes = await workflowsAPI.listWorkflowTemplates({
            is_active: true, target_entity_type: 'contact',
          });
          const allTemplates = (tmplRes.workflows || []).filter((t: any) =>
            !t.program_id || !programId || t.program_id === programId
          );
          setAvailableTemplates(allTemplates.map((t: any) => ({ id: t.id, name: t.name })));
        } catch { setAvailableTemplates([]); }
      },
      notes: async () => setNotes(await contactsAPI.listNotes(contactId)),
      goals: async () => setGoals(await contactsAPI.listGoals(contactId)),
      documents: async () => setDocuments(await contactsAPI.listDocuments(contactId)),
      forms: async () => setForms(await contactsAPI.listForms(contactId)),
      conversations: async () => { fetchTemplates(); fetchSentMessages(); },
    };
    if (loaders[activeTab]) loaders[activeTab]().catch(() => {});
  }, [activeTab, contactId]);

  // Communication helpers
  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      const res = await fetch(`${getApiBaseUrl()}/api/communication-templates`, {
        headers: { Authorization: `Bearer ${token}`, 'X-CBO-ID': cboId || '' },
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(Array.isArray(data) ? data : data.templates || []);
      }
    } catch {}
  };

  const fetchSentMessages = async () => {
    if (!contact?.id) return;
    setSentLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      const res = await fetch(
        `${getApiBaseUrl()}/api/staff/member-communications/member/${contact.id}/history`,
        { headers: { Authorization: `Bearer ${token}`, 'X-CBO-ID': cboId || '' } }
      );
      if (res.ok) {
        const data = await res.json();
        setSentMessages((Array.isArray(data) ? data : []).slice(0, 20));
      }
    } catch {} finally { setSentLoading(false); }
  };

  const handleTemplateSelect = (templateId: number) => {
    setSelectedTemplate(templateId);
    const tmpl = templates.find((t) => t.id === templateId);
    if (tmpl) {
      // Keep the {{variables}}: the server fills them when it sends.
      setMessageSubject(tmpl.subject || '');
      setMessageBody(tmpl.body || '');
    }
  };

  const handleSendMessage = async () => {
    if (!contact) return;
    if (selectedCommMethod === 'email' && !messageSubject.trim()) { alert(t('contacts.alertEnterSubject', 'Please enter a subject')); return; }
    if (!messageBody.trim()) { alert(t('contacts.alertEnterMessage', 'Please enter a message')); return; }
    if (sendInputs.missing.length > 0) {
      alert(t('templateVariables.inputs.missing', 'Fill in the required values: {{labels}}', { labels: sendInputs.missing.map((m) => m.label || m.key).join(', ') }));
      return;
    }
    setBlockedSends([]);
    try {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      const endpoint = selectedCommMethod === 'email'
        ? `${getApiBaseUrl()}/api/staff/member-communications/send-email`
        : `${getApiBaseUrl()}/api/staff/member-communications/send-sms`;
      const payload = selectedCommMethod === 'email'
        ? { member_ids: [contact.id], subject: messageSubject, message_body: messageBody, requires_response: false, variable_inputs: sendInputs.variableInputs }
        : { member_ids: [contact.id], message_body: messageBody, requires_response: false, variable_inputs: sendInputs.variableInputs };
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'X-CBO-ID': cboId || '', 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const result = await res.json().catch(() => ({}));
        const blocked = unfilledFailures(result?.failed);
        if (blocked.length > 0 && !(result?.total_sent > 0)) { setBlockedSends(blocked); return; }
        sendInputs.reset();
        setShowMessageComposer(false); setMessageSubject(''); setMessageBody(''); setSelectedTemplate(null);
        fetchSentMessages();
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || t('contacts.errorSendMessage', 'Failed to send message'));
      }
    } catch (err: any) { setError(err.message || t('contacts.errorSend', 'Failed to send')); }
  };

  const handleArchive = async () => {
    if (!confirm(t('contacts.confirmArchive', 'Archive this contact?'))) return;
    try { await contactsAPI.archiveContact(contactId); onArchived?.(); } catch {}
  };

  const handleDocView = async (doc: ContactDocument) => {
    const token = localStorage.getItem('auth_token') || '';
    const cboId = localStorage.getItem('active_cbo_id') || '';
    const base = getApiBaseUrl();
    try {
      const res = await fetch(`${base}/api/documents/${doc.uuid}`, {
        headers: { Authorization: `Bearer ${token}`, 'X-CBO-ID': cboId }, redirect: 'follow',
      });
      if (res.ok && res.redirected) window.open(res.url, '_blank');
      else { const data = await res.json().catch(() => null); window.open(data?.download_url || res.url, '_blank'); }
    } catch {}
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-blue-500 animate-spin" /></div>;
  if (!contact) return <div className="p-6"><button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 mb-4"><ArrowLeft className="w-4 h-4" /> {t('buttons.back', 'Back')}</button><div className="p-4 bg-red-50 text-red-700 rounded-lg">{error || t('contacts.notFound', 'Contact not found')}</div></div>;

  const pendingDupes = contact.duplicate_matches?.filter((d) => d.resolution === 'pending') || [];
  const memberObj = { id: contact.id, first_name: contact.first_name, last_name: contact.last_name, email: contact.email, phone_no: contact.phone_no, phone_number: contact.phone_no };

  const tabs: { key: TabKey; label: string; icon: React.ElementType }[] = [
    { key: 'overview', label: t('contacts.tabOverview', 'Overview'), icon: User },
    { key: 'activities', label: t('contacts.tabActivities', 'Activities'), icon: Activity },
    { key: 'documents', label: t('contacts.tabDocuments', 'Documents'), icon: FileText },
    { key: 'notes', label: t('contacts.tabNotes', 'Notes'), icon: StickyNote },
    { key: 'goals', label: t('contacts.tabGoals', 'Goals'), icon: Target },
    { key: 'tracking', label: t('contacts.tabTracking', 'Tracking'), icon: ListTodo },
    { key: 'hard-copy', label: t('contacts.tabHardCopy', 'Hard-Copy'), icon: Printer },
    { key: 'conversations', label: t('contacts.tabConversations', 'Conversations'), icon: MessageSquare },
    { key: 'forms', label: t('contacts.tabForms', 'Forms'), icon: ClipboardList },
    { key: 'giving', label: t('contacts.tabGiving', 'Giving'), icon: Gift },
    { key: 'history', label: t('contacts.tabHistory', 'History'), icon: Clock },
  ];

  return (
    <div className="space-y-5 p-3 sm:p-6">
      {/* Header */}
      <div>
        <button onClick={onBack} className="mb-3 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"><ArrowLeft className="h-4 w-4" /> {t('contacts.backToContacts', 'Back to Contacts')}</button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-shrink">
            <h1 className="truncate text-xl font-bold text-gray-900 dark:text-gray-100 sm:text-2xl">{contact.first_name} {contact.last_name || ''}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-500 sm:gap-3">
              <span className="truncate">{contact.member_number}</span>
              <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30">{t('contacts.badgeContact', 'Contact')}</span>
              {contact.intake_source && <span className="truncate text-xs">{contact.intake_source}</span>}
              {pendingDupes.length > 0 && <span className="inline-flex items-center gap-1 rounded bg-orange-100 px-2 py-0.5 text-xs text-orange-700"><AlertTriangle className="h-3 w-3" /> {pendingDupes.length} {pendingDupes.length === 1 ? t('contacts.duplicateSingular', 'duplicate') : t('contacts.duplicatePlural', 'duplicates')}</span>}
            </div>
          </div>
          <div className="flex flex-shrink-0 gap-2">
            <button onClick={() => setShowPromoteToMember(true)} className="flex items-center gap-1.5 rounded-lg border border-green-600 bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700"><ArrowUpRight className="h-4 w-4" /> <span className="hidden sm:inline">{t('contacts.promoteToMember', 'Promote to Member')}</span><span className="sm:hidden">{t('contacts.promote', 'Promote')}</span></button>
            <button onClick={() => onEdit?.(contactId)} className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50 dark:border-gray-600"><Edit className="h-4 w-4" /> <span className="hidden sm:inline">{t('common.edit', 'Edit')}</span></button>
            <button onClick={handleArchive} className="flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-2 text-sm text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /> <span className="hidden sm:inline">{t('contacts.archive', 'Archive')}</span></button>
          </div>
        </div>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">{t('contacts.colContactInfo', 'Contact Info')}</h3>
          <div className="space-y-1.5 text-sm">
            {contact.phone_no && <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300"><Phone className="w-3.5 h-3.5 text-gray-400" /> {contact.phone_no}</div>}
            {contact.email && <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300"><Mail className="w-3.5 h-3.5 text-gray-400" /> {contact.email}</div>}
            {contact.date_of_birth && <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300"><Calendar className="w-3.5 h-3.5 text-gray-400" /> {contact.date_of_birth}</div>}
            {(contact.street_address || contact.city) && <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300"><MapPin className="w-3.5 h-3.5 text-gray-400" /> {[contact.street_address, contact.city, contact.state, contact.zip_code].filter(Boolean).join(', ')}</div>}
          </div>
          {(contact.submitted_by_name || contact.submitted_by_email || contact.submitted_by_phone) && (
            <div className="mt-3 border-t border-gray-100 pt-2 dark:border-gray-700">
              <div className="text-xs font-semibold uppercase text-gray-400">{t('contacts.colSubmittedBy', 'Submitted By')}</div>
              <div className="mt-1 space-y-0.5 text-sm text-gray-700 dark:text-gray-300">
                {contact.submitted_by_name && <div>{contact.submitted_by_name}</div>}
                {contact.submitted_by_email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-gray-400" /> {contact.submitted_by_email}</div>}
                {contact.submitted_by_phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-gray-400" /> {contact.submitted_by_phone}</div>}
              </div>
            </div>
          )}
        </div>
        <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">{t('contacts.demographics', 'Demographics')}</h3>
          <div className="grid grid-cols-1 gap-1.5 text-sm text-gray-600 dark:text-gray-400 sm:grid-cols-2">
            {contact.gender && <div><span className="text-gray-400">{t('contacts.gender', 'Gender')}:</span> {contact.gender}</div>}
            {contact.race && <div><span className="text-gray-400">{t('contacts.race', 'Race')}:</span> {contact.race}</div>}
            {contact.ethnicity && <div><span className="text-gray-400">{t('contacts.ethnicity', 'Ethnicity')}:</span> {contact.ethnicity}</div>}
            {contact.language && <div><span className="text-gray-400">{t('contacts.language', 'Language')}:</span> {contact.language}</div>}
          </div>
        </div>
        <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">{t('contacts.dataQuality', 'Data Quality')}</h3>
          {contact.data_quality_score != null ? (
            <div className="flex items-center gap-2"><div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden"><div className={`h-full rounded-full ${contact.data_quality_score >= 70 ? 'bg-green-500' : contact.data_quality_score >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${contact.data_quality_score}%` }} /></div><span className="text-sm font-medium">{contact.data_quality_score}%</span></div>
          ) : <p className="text-sm text-gray-400">{t('contacts.notScored', 'Not scored')}</p>}
        </div>
      </div>

      {/* Program Pipelines */}
      {contact.program_statuses && contact.program_statuses.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('contacts.programPipelines', 'Program Pipelines')}</h3>
          {contact.program_statuses.map((ps) => (
            <div key={ps.id} className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div><span className="font-medium text-gray-900 dark:text-gray-100">{ps.program_name}</span>{ps.assigned_to_name && <span className="ml-2 text-xs text-gray-400">{t('contacts.assignedLabel', 'Assigned')}: {ps.assigned_to_name}</span>}</div>
                {!ps.promoted_to_enrollment_id ? <button onClick={() => setPromoteProgram(ps)} className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200"><ArrowUpRight className="w-3.5 h-3.5" /> {t('contacts.enroll', 'Enroll')}</button> : <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">{t('contacts.statusEnrolled', 'Enrolled')}</span>}
              </div>
              <ContactStatusPipeline contactId={contactId} programStatus={ps} onStatusChange={() => loadContact()} onAdvanceError={(msg) => setError(msg)} />
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        <div className="flex gap-4">{tabs.map((tab) => { const Icon = tab.icon; return (<button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`flex items-center gap-1.5 pb-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${activeTab === tab.key ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}><Icon className="w-4 h-4" /> {tab.label}</button>); })}</div>
      </div>

      {/* OVERVIEW */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 text-sm text-gray-600 dark:text-gray-400 sm:grid-cols-2 sm:gap-4">
            <div><span className="text-gray-400">{t('contacts.created', 'Created')}:</span> {contact.created_at ? new Date(contact.created_at).toLocaleString() : '-'}</div>
            <div><span className="text-gray-400">{t('contacts.intakeSource', 'Intake Source')}:</span> {contact.intake_source || '-'}</div>
            <div><span className="text-gray-400">{t('contacts.language', 'Language')}:</span> {contact.preferred_language || '-'}</div>
            <div><span className="text-gray-400">{t('contacts.country', 'Country')}:</span> {contact.country || '-'}</div>
          </div>

          {/* Custom Fields */}
          {contact.custom_fields && typeof contact.custom_fields === 'object' && Object.keys(contact.custom_fields).length > 0 && (
            <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
              <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">{t('contacts.customFields', 'Custom Fields')}</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                {Object.entries(contact.custom_fields).map(([key, value]) => (
                  <div key={key}>
                    <span className="text-gray-400 text-xs">{key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</span>
                    <div className="text-gray-700 dark:text-gray-300">{String(value) || '-'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No program assigned — show assign action */}
          {(!contact.program_statuses || contact.program_statuses.length === 0) && (
            <div className="p-4 border-2 border-dashed border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-900/10 rounded-lg">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('contacts.noProgramAssigned', 'This contact is not assigned to a program pipeline.')}</p>
              <p className="text-xs text-gray-400 mb-3">{t('contacts.noProgramAssignedHint', 'Assign to a program to start tracking their lifecycle, enable workflows, and set status requirements.')}</p>
              <div className="flex items-center gap-2">
                <select
                  value={selectedProgramId ?? ''}
                  onChange={(e) => setSelectedProgramId(e.target.value ? Number(e.target.value) : null)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-gray-100"
                >
                  <option value="">{t('contacts.selectProgram', 'Select a program...')}</option>
                  {availablePrograms.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <button
                  onClick={async () => {
                    if (!selectedProgramId) return;
                    setAssigningProgram(true);
                    try {
                      await contactsAPI.updateContactStatus(contactId, selectedProgramId, { new_status: 'new' });
                      await loadContact();
                      setSelectedProgramId(null);
                    } catch (err: any) {
                      setError(err.response?.data?.detail || t('contacts.errorAssignProgram', 'Failed to assign program'));
                    } finally {
                      setAssigningProgram(false);
                    }
                  }}
                  disabled={!selectedProgramId || assigningProgram}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {assigningProgram ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {t('contacts.assign', 'Assign')}
                </button>
              </div>
            </div>
          )}

          {pendingDupes.length > 0 && <div className="p-4 border border-orange-200 dark:border-orange-800 rounded-lg"><DuplicateMatchPanel contactId={contactId} matches={contact.duplicate_matches || []} onResolve={async (mid, res, n) => { await contactsAPI.resolveDuplicate(contactId, mid, { resolution: res, notes: n }); loadContact(); return true; }} onMerge={async (tid) => { await contactsAPI.mergeContact(contactId, tid); onBack?.(); return true; }} onRefresh={loadContact} /></div>}
        </div>
      )}

      {/* ACTIVITIES — workflow status first, then activity log */}
      {activeTab === 'activities' && (
        <div className="space-y-6">
          {/* Workflow Status Section (Primary) */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{t('contacts.workflowStatus', 'Workflow Status')}</h2>

            {/* Active Workflow Progress */}
            {workflows.length > 0 ? (
              <div className="space-y-3">
                {workflows.map((wf) => (
                  <div key={wf.id} className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{wf.template_name || `${t('contacts.workflow', 'Workflow')} #${wf.id}`}</span>
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${wf.status === 'active' ? 'bg-green-100 text-green-700' : wf.status === 'completed' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{wf.status}</span>
                    </div>
                    {wf.progress_percentage != null && (
                      <div className="flex items-center gap-2 mb-3">
                        <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full" style={{ width: `${wf.progress_percentage}%` }} />
                        </div>
                        <span className="text-xs text-gray-500">{wf.progress_percentage}%</span>
                      </div>
                    )}
                    <MemberWorkflowProgress workflowInstanceId={wf.id}
                      onStepExecute={() => { contactsAPI.listWorkflows(contactId).then(setWorkflows); contactsAPI.listContactActivities(contactId).then((r) => setActivities(r.items)); }}
                      onNavigateToCommunication={(channel: any) => { setSelectedCommMethod(channel); setActiveTab('conversations'); }}
                      onNavigateToForm={() => setActiveTab('forms')}
                      onNavigateToDocuments={() => { setShowDocUpload(true); setActiveTab('documents'); }}
                      onAddActivity={() => setShowActivityForm(true)} />
                  </div>
                ))}
              </div>
            ) : (
              /* No workflows — show start options or empty state */
              <div className="p-6 bg-white dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-center">
                <Activity className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('contacts.noActiveWorkflow', 'No Active Workflow')}</h3>
                {availableTemplates.length > 0 ? (
                  <>
                    <p className="text-sm text-gray-500 mb-4">{t('contacts.startWorkflowHint', 'Start a workflow to track this contact through structured steps.')}</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {availableTemplates.map((tmpl) => (
                        <button key={tmpl.id} onClick={async () => {
                          try {
                            await workflowsAPI.createWorkflowInstance({ workflow_template_id: tmpl.id, member_id: contactId });
                            const wfRes = await contactsAPI.listWorkflows(contactId);
                            setWorkflows(wfRes);
                          } catch (err: any) { setError(err.response?.data?.detail || t('contacts.errorStartWorkflow', 'Failed to start workflow')); }
                        }} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                          <Plus className="w-4 h-4" /> {t('contacts.startPrefix', 'Start')}: {tmpl.name}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-500">{t('contacts.noWorkflowTemplates', 'No workflow templates available for contacts in this program. Create one in Administration > Agent Workflow Templates with target type "Contacts".')}</p>
                )}
              </div>
            )}
          </div>

          {/* Activity Log Section (Secondary) */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('contacts.activityLog', 'Activity Log')}</h3>
              <button onClick={() => setShowActivityForm(!showActivityForm)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"><Plus className="w-3.5 h-3.5" /> {t('contacts.logActivity', 'Log Activity')}</button>
            </div>

            {/* Add Activity Form (collapsible) */}
            {showActivityForm && (
              <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg mb-3">
                <ContactActivityForm contactId={contactId} programId={contact.program_statuses?.[0]?.program_id}
                  onSubmit={async (data) => { await contactsAPI.createContactActivity(contactId, data); const r = await contactsAPI.listContactActivities(contactId); setActivities(r.items); return true; }}
                  onClose={() => setShowActivityForm(false)} />
              </div>
            )}
          </div>

          {/* Activity Log Table (enrollment-matching) */}
          {activities.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">{t('contacts.activityLog', 'Activity Log')}</h3>
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800"><tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">{t('contacts.colType', 'Type')}</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">{t('contacts.colDate', 'Date')}</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">{t('contacts.status', 'Status')}</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">{t('contacts.colPerformedBy', 'Performed By')}</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">{t('contacts.notes', 'Notes')}</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {activities.map((a) => (
                      <tr key={a.id}>
                        <td className="px-4 py-2 capitalize">{a.activity_type?.replace(/_/g, ' ')}</td>
                        <td className="px-4 py-2 text-gray-500">{a.activity_date ? new Date(a.activity_date).toLocaleDateString() : '-'}</td>
                        <td className="px-4 py-2">
                          <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                            a.status === 'completed' ? 'bg-green-100 text-green-700' :
                            a.contact_outcome === 'reached' ? 'bg-green-100 text-green-700' :
                            a.contact_outcome === 'voicemail' ? 'bg-amber-100 text-amber-700' :
                            a.contact_outcome === 'no_answer' ? 'bg-gray-100 text-gray-500' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {a.contact_outcome || a.status || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-gray-500">{a.performed_by_name || '-'}</td>
                        <td className="px-4 py-2 text-gray-400 max-w-md whitespace-pre-wrap break-words align-top">{a.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Empty state */}
          {activities.length === 0 && workflows.length === 0 && availableTemplates.length === 0 && (
            <div className="text-center py-8 text-gray-400"><Activity className="w-8 h-8 mx-auto mb-2 opacity-40" /><p className="text-sm">{t('contacts.noActivitiesOrWorkflows', 'No activities or workflows yet')}</p></div>
          )}
        </div>
      )}

      {/* DOCUMENTS — with DocumentUploadDialog + requirements + view/delete */}
      {activeTab === 'documents' && (
        <div className="space-y-4">
          <DocumentRequirements context="enrollment" contextId={contactId} onUploadForType={() => setShowDocUpload(true)} />
          <div className="flex justify-between items-center">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('contacts.uploadedDocuments', 'Uploaded Documents')}</h4>
            <button onClick={() => setShowDocUpload(true)} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"><FileText className="w-4 h-4" /> {t('contacts.uploadDocument', 'Upload Document')}</button>
          </div>
          <DocumentUploadDialog
            open={showDocUpload}
            onOpenChange={setShowDocUpload}
            targetType="member"
            targetId={contactId}
            targetName={`${contact.first_name} ${contact.last_name || ''}`.trim()}
            onUploadComplete={async () => { setShowDocUpload(false); setDocuments(await contactsAPI.listDocuments(contactId)); }}
          />
          {documents.length === 0 ? <div className="text-center py-8 text-gray-400"><FileText className="w-8 h-8 mx-auto mb-2 opacity-40" /><p className="text-sm">{t('contacts.noDocuments', 'No documents uploaded yet')}</p></div> : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full min-w-[720px] text-sm"><thead className="bg-gray-50 dark:bg-gray-800"><tr><th className="px-4 py-2 text-left font-medium text-gray-600">{t('contacts.colType', 'Type')}</th><th className="px-4 py-2 text-left font-medium text-gray-600">{t('contacts.colFileName', 'File Name')}</th><th className="px-4 py-2 text-left font-medium text-gray-600">{t('contacts.colUploadDate', 'Upload Date')}</th><th className="px-4 py-2 text-left font-medium text-gray-600">{t('contacts.colUploadedBy', 'Uploaded By')}</th><th className="px-4 py-2 text-center font-medium text-gray-600">{t('contacts.colActions', 'Actions')}</th></tr></thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">{documents.map((d) => (
                <tr key={d.id}>
                  <td className="px-4 py-2"><span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">{d.doc_type_name || `${t('contacts.colType', 'Type')} #${d.doc_type_id}`}</span></td>
                  <td className="px-4 py-2 flex items-center gap-1.5">{d.mime_type?.startsWith('image/') && <ImageIcon className="w-3.5 h-3.5 text-gray-400" />}{d.file_name || t('contacts.untitled', 'Untitled')}</td>
                  <td className="px-4 py-2 text-gray-400 text-xs">{d.created_at ? new Date(d.created_at).toLocaleDateString() : '-'}</td>
                  <td className="px-4 py-2 text-gray-400 text-xs">{d.created_by_name || '-'}</td>
                  <td className="px-4 py-2 text-center"><div className="flex items-center justify-center gap-1">
                    {d.uuid && <button onClick={() => handleDocView(d)} className="p-1 hover:bg-gray-100 rounded" title={t('buttons.view', 'View')}><Eye className="w-3.5 h-3.5 text-gray-400" /></button>}
                    <button onClick={async () => { await contactsAPI.deleteDocument(contactId, d.id); setDocuments(await contactsAPI.listDocuments(contactId)); }} className="p-1 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                  </div></td>
                </tr>
              ))}</tbody></table>
            </div>
          )}
        </div>
      )}

      {/* NOTES */}
      {activeTab === 'notes' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center"><h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('contacts.caseNotes', 'Case Notes')}</h4><button onClick={() => setShowNoteForm(!showNoteForm)} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"><Plus className="w-4 h-4" /> {t('contacts.addNote', 'Add Note')}</button></div>
          {showNoteForm && (
            <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div><label className="block text-xs font-medium text-gray-600 mb-1">{t('contacts.colType', 'Type')}</label><select value={noteForm.note_type} onChange={(e) => setNoteForm((p) => ({ ...p, note_type: e.target.value }))} className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-gray-100"><option value="general">{t('contacts.noteTypeGeneral', 'General')}</option><option value="outreach">{t('contacts.statusOutreach', 'Outreach')}</option><option value="intake">{t('contacts.noteTypeIntake', 'Intake')}</option><option value="confidential">{t('contacts.confidential', 'Confidential')}</option></select></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">{t('contacts.subject', 'Subject')}</label><input value={noteForm.subject || ''} onChange={(e) => setNoteForm((p) => ({ ...p, subject: e.target.value }))} className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-gray-100" placeholder={t('contacts.optional', 'Optional')} /></div>
              </div>
              <textarea value={noteForm.note_text} onChange={(e) => setNoteForm((p) => ({ ...p, note_text: e.target.value }))} rows={3} className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-gray-100" placeholder={t('contacts.noteTextPlaceholder', 'Note text...')} />
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={noteForm.is_confidential} onChange={(e) => setNoteForm((p) => ({ ...p, is_confidential: e.target.checked }))} className="rounded border-gray-300" /> {t('contacts.confidential', 'Confidential')}</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={noteForm.requires_followup} onChange={(e) => setNoteForm((p) => ({ ...p, requires_followup: e.target.checked }))} className="rounded border-gray-300" /> {t('contacts.requiresFollowup', 'Requires Follow-up')}</label>
              </div>
              <div className="flex justify-end gap-2"><button onClick={() => setShowNoteForm(false)} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">{t('common.cancel', 'Cancel')}</button><button onClick={async () => { await contactsAPI.createNote(contactId, noteForm); setNotes(await contactsAPI.listNotes(contactId)); setShowNoteForm(false); setNoteForm({ note_text: '', note_type: 'general' }); }} disabled={!noteForm.note_text.trim()} className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">{t('contacts.saveNote', 'Save Note')}</button></div>
            </div>
          )}
          {notes.length === 0 ? <div className="text-center py-8 text-gray-400"><StickyNote className="w-8 h-8 mx-auto mb-2 opacity-40" /><p className="text-sm">{t('contacts.noNotes', 'No notes yet')}</p></div> : (
            <div className="space-y-3">{notes.map((n) => (
              <div key={n.id} className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="flex items-center justify-between mb-2"><div className="flex items-center gap-2"><span className={`text-xs px-2 py-0.5 rounded font-medium ${n.note_type === 'confidential' ? 'bg-red-100 text-red-700' : n.note_type === 'outreach' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{n.note_type}</span>{n.subject && <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{n.subject}</span>}{n.is_confidential && <Lock className="w-3.5 h-3.5 text-red-400" />}{n.requires_followup && <Clock className="w-3.5 h-3.5 text-amber-500" />}</div><span className="text-xs text-gray-400">{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</span></div>
                <p className="text-sm text-gray-700 dark:text-gray-300">{n.note_text}</p>
                {n.created_by_name && <p className="text-xs text-gray-400 mt-1">{t('contacts.by', 'by')} {n.created_by_name}</p>}
              </div>
            ))}</div>
          )}
        </div>
      )}

      {/* GOALS */}
      {activeTab === 'goals' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center"><h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('contacts.goals', 'Goals')}</h4><button onClick={() => setShowGoalForm(!showGoalForm)} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"><Plus className="w-4 h-4" /> {t('contacts.addGoal', 'Add Goal')}</button></div>
          {showGoalForm && (
            <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg space-y-3">
              <input value={goalForm.goal_text} onChange={(e) => setGoalForm((p) => ({ ...p, goal_text: e.target.value }))} className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-gray-100" placeholder={t('contacts.goalDescriptionPlaceholder', 'Goal description')} />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <select value={goalForm.goal_category || ''} onChange={(e) => setGoalForm((p) => ({ ...p, goal_category: e.target.value }))} className="px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-gray-100"><option value="">{t('contacts.categoryPlaceholder', 'Category...')}</option><option value="health">{t('contacts.categoryHealth', 'Health')}</option><option value="social">{t('contacts.categorySocial', 'Social')}</option><option value="employment">{t('contacts.categoryEmployment', 'Employment')}</option><option value="education">{t('contacts.categoryEducation', 'Education')}</option><option value="housing">{t('contacts.categoryHousing', 'Housing')}</option><option value="other">{t('contacts.categoryOther', 'Other')}</option></select>
                <input type="date" value={goalForm.target_date || ''} onChange={(e) => setGoalForm((p) => ({ ...p, target_date: e.target.value }))} className="px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-gray-100" />
              </div>
              <div className="flex justify-end gap-2"><button onClick={() => setShowGoalForm(false)} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">{t('common.cancel', 'Cancel')}</button><button onClick={async () => { await contactsAPI.createGoal(contactId, goalForm); setGoals(await contactsAPI.listGoals(contactId)); setShowGoalForm(false); setGoalForm({ goal_text: '' }); }} disabled={!goalForm.goal_text.trim()} className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">{t('contacts.saveGoal', 'Save Goal')}</button></div>
            </div>
          )}
          {goals.length === 0 ? <div className="text-center py-8 text-gray-400"><Target className="w-8 h-8 mx-auto mb-2 opacity-40" /><p className="text-sm">{t('contacts.noGoals', 'No goals yet')}</p></div> : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700"><table className="w-full min-w-[640px] text-sm"><thead className="bg-gray-50 dark:bg-gray-800"><tr><th className="px-4 py-2 text-left font-medium text-gray-600">{t('contacts.colGoal', 'Goal')}</th><th className="px-4 py-2 text-left font-medium text-gray-600">{t('contacts.colCategory', 'Category')}</th><th className="px-4 py-2 text-left font-medium text-gray-600">{t('contacts.colTarget', 'Target')}</th><th className="px-4 py-2 text-left font-medium text-gray-600">{t('contacts.status', 'Status')}</th><th className="px-4 py-2 text-center font-medium text-gray-600">{t('contacts.colActions', 'Actions')}</th></tr></thead><tbody className="divide-y divide-gray-100 dark:divide-gray-700">{goals.map((g) => (
              <tr key={g.id}><td className="px-4 py-2">{g.goal_text}</td><td className="px-4 py-2 text-gray-500 capitalize">{g.goal_category || '-'}</td><td className="px-4 py-2 text-gray-400 text-xs">{g.target_date || '-'}</td><td className="px-4 py-2"><span className={`text-xs px-2 py-0.5 rounded ${g.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{g.status}</span></td><td className="px-4 py-2 text-center">{g.status === 'active' && <button onClick={async () => { await contactsAPI.updateGoal(contactId, g.id, { status: 'completed' }); setGoals(await contactsAPI.listGoals(contactId)); }} className="text-xs text-green-600 hover:underline">{t('contacts.complete', 'Complete')}</button>}</td></tr>
            ))}</tbody></table></div>
          )}
        </div>
      )}

      {/* TRACKING — direct reuse of TaskList (same as enrollment) */}
      {activeTab === 'tracking' && <TaskList memberId={contactId} memberName={`${contact.first_name} ${contact.last_name || ''}`.trim()} />}

      {/* HARD-COPY TRACKING — offline document lifecycle */}
      {activeTab === 'hard-copy' && (
        <HardCopyTrackingList
          memberId={contactId}
          memberName={`${contact.first_name} ${contact.last_name || ''}`.trim()}
          targetType="member"
          targetId={contactId}
          showCreate
        />
      )}

      {/* CONVERSATIONS — full email/sms/phone with templates + sent history */}
      {activeTab === 'conversations' && (
        <div className="space-y-6">
          {/* Method buttons */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(['email', 'sms', 'phone'] as const).map((ch) => {
              const icons = { email: Mail, sms: MessageSquare, phone: Phone };
              const labels = { email: t('contacts.methodEmail', 'Email'), sms: t('contacts.methodSms', 'SMS'), phone: t('contacts.methodPhoneCall', 'Phone Call') };
              const info = ch === 'email' ? (contact.email || t('contacts.noEmail', 'No email')) : (contact.phone_no || t('contacts.noPhone', 'No phone'));
              const disabled = ch === 'email' ? !contact.email : !contact.phone_no;
              const Icon = icons[ch];
              return (
                <button key={ch} onClick={() => { setSelectedCommMethod(ch); if (ch !== 'phone') setShowMessageComposer(true); }} disabled={disabled}
                  className={`flex items-center gap-3 p-4 rounded-lg border text-left ${selectedCommMethod === ch ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                  <Icon className="w-5 h-5 text-gray-600" /><div><div className="font-medium text-sm">{labels[ch]}</div><div className="text-xs text-gray-400">{info}</div></div>
                </button>
              );
            })}
          </div>

          {/* Phone interface */}
          {selectedCommMethod === 'phone' && contact.phone_no && (
            <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
              <MemberPhoneInterface member={memberObj as any} enrollmentId={undefined} />
            </div>
          )}

          {/* Message composer with templates */}
          {showMessageComposer && (selectedCommMethod === 'email' || selectedCommMethod === 'sms') && (
            <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg space-y-4">
              <div className="flex justify-between items-center"><h3 className="text-sm font-semibold">{selectedCommMethod === 'email' ? t('contacts.composeEmail', 'Compose Email') : t('contacts.composeSms', 'Compose SMS')}</h3><button onClick={() => { setShowMessageComposer(false); setMessageSubject(''); setMessageBody(''); setSelectedTemplate(null); }} className="text-xs text-gray-400 hover:text-gray-600">{t('common.cancel', 'Cancel')}</button></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">{t('contacts.useTemplate', 'Use Template (Optional)')}</label>
                <select value={selectedTemplate?.toString() || ''} onChange={(e) => e.target.value ? handleTemplateSelect(Number(e.target.value)) : setSelectedTemplate(null)} className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-gray-100">
                  <option value="">{t('contacts.noTemplate', '-- No Template --')}</option>
                  {templates.filter((t) => selectedCommMethod === 'email' ? t.communication_method === 'email' : t.communication_method === 'sms').map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              {selectedCommMethod === 'email' && <div><label className="block text-xs font-medium text-gray-600 mb-1">{t('contacts.subject', 'Subject')}</label><input value={messageSubject} onChange={(e) => setMessageSubject(e.target.value)} className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-gray-100" required /></div>}
              <div><label className="block text-xs font-medium text-gray-600 mb-1">{t('contacts.message', 'Message')}</label><textarea ref={messageBodyRef} value={messageBody} onChange={(e) => setMessageBody(e.target.value)} rows={5} className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-gray-100" placeholder={selectedCommMethod === 'sms' ? t('contacts.typeSmsPlaceholder', 'Type your SMS...') : t('contacts.typeEmailPlaceholder', 'Type your email...')} required />
                {selectedCommMethod === 'sms' && <p className={`text-xs mt-1 ${messageBody.length > 160 ? 'text-red-500' : 'text-gray-400'}`}>{messageBody.length} {t('contacts.chars', 'chars')}{messageBody.length > 160 && t('contacts.multipleSegments', ' (multiple segments)')}</p>}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <VariablePicker appliesTo="messages" onInsert={(key) => setMessageBody((b) => insertToken(messageBodyRef.current, b, key))} />
                <TemplatePreviewButton
                  appliesTo="messages"
                  subject={selectedCommMethod === 'email' ? messageSubject : null}
                  body={messageBody}
                  member={contact ? { id: contact.id, name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim() } : null}
                  variableInputs={sendInputs.values}
                />
              </div>
              <VariableInputFields inputs={sendInputs.inputs} values={sendInputs.values} onChange={sendInputs.setValues} idPrefix="contact-send-input" />
              {blockedSends.length > 0 && (
                <UnfilledMergeFields
                  failures={blockedSends}
                  memberNames={contact ? { [contact.id]: `${contact.first_name || ''} ${contact.last_name || ''}`.trim() } : undefined}
                />
              )}
              <div className="flex justify-end"><button onClick={handleSendMessage} disabled={!messageBody.trim()} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"><Send className="w-4 h-4" /> {selectedCommMethod === 'email' ? t('contacts.sendEmail', 'Send Email') : t('contacts.sendSms', 'Send SMS')}</button></div>
            </div>
          )}

          {/* Conversation history */}
          <div>
            <div className="flex justify-between items-center mb-3"><h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('contacts.conversationHistory', 'Conversation History')}</h4><button onClick={fetchSentMessages} className="text-xs text-blue-600 hover:underline">{t('contacts.refresh', 'Refresh')}</button></div>
            {sentLoading ? <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 text-blue-500 animate-spin" /></div>
            : sentMessages.length === 0 ? <div className="text-center py-8 text-gray-400"><MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" /><p className="text-sm">{t('contacts.noConversationHistory', 'No conversation history yet. Use the buttons above to send SMS or email.')}</p></div>
            : (
              <div className="space-y-2">{sentMessages.map((msg) => (
                <div key={msg.id} className="p-3 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${msg.direction === 'inbound' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                        {msg.communication_type === 'email' || msg.subject ? <Mail className="w-3 h-3 inline mr-1" /> : <MessageSquare className="w-3 h-3 inline mr-1" />}
                        {msg.direction === 'inbound' ? t('contacts.received', 'Received') : t('contacts.sent', 'Sent')}
                      </span>
                      {msg.subject && <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{msg.subject}</span>}
                    </div>
                    <span className="text-xs text-gray-400">{msg.created_at ? new Date(msg.created_at).toLocaleString() : ''}</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">{msg.message_body || msg.content}</p>
                </div>
              ))}</div>
            )}
          </div>
        </div>
      )}

      {/* FORMS */}
      {activeTab === 'forms' && (
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('contacts.forms', 'Forms')}</h4>
          {forms.length === 0 ? <div className="text-center py-8 text-gray-400"><ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-40" /><p className="text-sm">{t('contacts.noForms', 'No forms assigned')}</p></div> : (
            <div className="space-y-2">{forms.map((f) => (
              // TRUS-636: open the form on its own page (new tab), where the
              // answers can be read and printed. Includes the public form
              // submission that created this contact.
              <button
                key={f.id}
                type="button"
                onClick={() => window.open(formPageUrl({ instanceId: f.id, memberId: contactId }), '_blank', 'noopener')}
                title={t('contacts.openForm', 'Open form to view or print')}
                className="w-full text-left flex items-center justify-between p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-400 hover:bg-blue-50/40 dark:hover:bg-gray-700 transition-colors"
              >
                <div><span className="font-medium text-sm text-gray-900 dark:text-gray-100">{f.template_name}</span><span className="ml-2 text-xs text-gray-400">{f.form_type}</span>{f.submitted_at && <span className="ml-2 text-xs text-gray-400">{new Date(f.submitted_at).toLocaleDateString()}</span>}</div>
                <div className="flex items-center gap-2"><span className={`text-xs px-2 py-0.5 rounded ${f.status === 'approved' ? 'bg-green-100 text-green-700' : f.status === 'submitted' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{f.status}</span>{f.completion_percentage != null && <span className="text-xs text-gray-400">{f.completion_percentage}%</span>}<ExternalLink className="w-3.5 h-3.5 text-gray-400" /></div>
              </button>
            ))}</div>
          )}
        </div>
      )}

      {/* GIVING / ENGAGEMENT (Fundraising Suite) */}
      {activeTab === 'giving' && <DonorGivingTab contactId={contactId} />}

      {/* HISTORY / AUDIT TRAIL */}

      {activeTab === 'history' && (
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('contacts.changeHistory', 'Change History')}</h4>
          <AuditTrailPanel entityType="contact" entityId={contactId} apiPath={`/api/contacts/${contactId}/audit-log`} />
        </div>
      )}

      {/* Error toast */}
      {error && <div className="fixed bottom-4 right-4 p-3 bg-red-600 text-white text-sm rounded-lg shadow-lg max-w-sm z-50">{error} <button onClick={() => setError(null)} className="ml-2 text-red-200 hover:text-white">&times;</button></div>}

      {/* Promote dialog — on success the contact has been promoted to a member
          (same id, record_type now 'member'). Re-fetching it as a contact would
          404 ("Contact not found"), so hand off to the member profile instead. */}
      {promoteProgram && <PromoteEnrollDialog contact={contact} programStatus={promoteProgram} onClose={() => setPromoteProgram(null)} onSuccess={() => { setPromoteProgram(null); if (onEnrolled) { onEnrolled(contactId); } else { loadContact(); } }} />}

      {/* Promote to Member (no enrollment) with dedup warning. On success the
          record is a member (same id on promote, or the merge target id). */}
      {showPromoteToMember && <PromoteToMemberDialog contact={contact} onClose={() => setShowPromoteToMember(false)} onSuccess={(memberId) => { setShowPromoteToMember(false); if (onEnrolled) { onEnrolled(memberId); } else { loadContact(); } }} />}
    </div>
  );
}
